// Shared utilities for MIFO application

/**
 * Format currency for display
 * @param {number} amount - Amount in cents
 * @param {string} currency - Currency code (default: EUR)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency = 'EUR') {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency
  }).format(amount / 100)
}

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale (default: de-DE)
 * @returns {string} Formatted date string
 */
export function formatDate(date, locale = 'de-DE') {
  if (!date) return '-'
  
  try {
    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) {
      return '-'
    }
    
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(dateObj)
  } catch (error) {
    console.error('Error formatting date:', error)
    return '-'
  }
}

/**
 * Generate invoice ID
 * @param {string} orderId - Order ID
 * @param {number} sequenceNumber - Sequence number
 * @returns {string} Invoice ID
 */
export function generateInvoiceId(orderId, sequenceNumber = 1) {
  return `INV-${orderId}-${sequenceNumber.toString().padStart(2, '0')}`
}

/**
 * Calculate VAT amount
 * @param {number} amount - Net amount in cents
 * @param {number} vatRate - VAT rate as decimal (default: 0.19)
 * @returns {number} VAT amount in cents
 */
export function calculateVAT(amount, vatRate = 0.19) {
  return Math.round(amount * vatRate)
}

/**
 * Calculate total amount including VAT
 * @param {number} netAmount - Net amount in cents
 * @param {number} vatRate - VAT rate as decimal (default: 0.19)
 * @returns {number} Total amount in cents
 */
export function calculateTotal(netAmount, vatRate = 0.19) {
  return netAmount + calculateVAT(netAmount, vatRate)
}

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email
 */
export function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate German postal code
 * @param {string} postalCode - Postal code to validate
 * @returns {boolean} True if valid German postal code
 */
export function isValidGermanPostalCode(postalCode) {
  const postalCodeRegex = /^[0-9]{5}$/
  return postalCodeRegex.test(postalCode)
}

/**
 * Sanitize string for database storage
 * @param {string} str - String to sanitize
 * @returns {string} Sanitized string
 */
export function sanitizeString(str) {
  if (typeof str !== 'string') return str
  return str.trim().replace(/[<>]/g, '')
} 